import { ApiError, toApiError } from './api-error';

const API_BASE_URL = import.meta.env.VITE_API_URL || 'http://localhost:3002';

/**
 * Delivery providers (spec "Delivery integrations v1 — Picker Express" §3, §4.5): a rider service the
 * brand connects with a master key and links to each branch with one of its stores. A sibling of the
 * POS connection, never one of its rows. The vocabulary is provider-neutral on purpose — Picker is
 * only the first `type`; Logística 21 (a Montevideo cadetería, spec "Delivery integrations v2") the
 * second.
 */
export type DeliveryProviderType = 'picker' | 'logistica21';

/**
 * What a vendor can do, as the API names it (`capabilities` on the provider row and on every
 * `GET /deliveries/capabilities` entry). Read through {@link supportsOf}, never compared one by one.
 */
export type DeliveryProviderCapability =
  | 'QUOTE'
  | 'DISPATCH'
  | 'WEBHOOKS'
  | 'LIST_STORES'
  | 'START_SEARCH'
  | 'CANCEL_REASONS'
  | 'COVERAGE_CHECK'
  | 'HOLD'
  | 'ACCOUNT_WEBHOOKS'
  | 'WEBHOOK_REGISTRATION'
  | 'REASSIGNS'
  | 'DELIVERY_CODE'
  | 'OWN_FLEET'
  | 'IDEMPOTENT_CREATE';

/** One credential the connect form asks for. Labels are `locations` i18n keys. */
export interface DeliveryCredentialField {
  /** Key inside `credentials` on validate / connect (`{ masterKey }` for Picker, spec §4.5). */
  key: string;
  labelKey: string;
  hintKey?: string;
  placeholderKey?: string;
  type: 'password' | 'text';
}

/** What the UI does differently per vendor — derived from the API's capabilities by {@link supportsOf}. */
export interface DeliveryProviderSupports {
  /** The master credentials list the workspace's stores (`GET /delivery-providers/:brandId/stores`). */
  stores: boolean;
  /** Cancelling takes a reason from the vendor's own list (`GET /deliveries/cancel-reasons`). */
  cancelReasons: boolean;
  /** The quote carries a price (`QUOTE`). Without it a quote answers coverage only (`fee: null`). */
  quote: boolean;
  /** The vendor itself waits out the prep time (`HOLD`); without it Menteo holds the booking. */
  hold: boolean;
  /** "Tengo flota propia" means something: `terminal` can travel with the brand's own riders (`OWN_FLEET`). */
  ownFleet: boolean;
  /** The booking's `validationCode` is a code the customer gives the rider at the door (`DELIVERY_CODE`). */
  deliveryCode: boolean;
  /** The quote answers whether the address is reachable, without a price (`COVERAGE_CHECK`). */
  coverage: boolean;
}

/**
 * Everything the UI needs to know about a delivery vendor, so no component ever branches on a
 * vendor's name. A second vendor is one entry here plus its i18n (`locations:delivery.providers.<id>`)
 * — the connect form, the bindings table, the "Nuevo envío" integration select and the board render
 * from this list. `supports` and `courierLeadMinutes` are only the fallback for an API that does not
 * send them yet: read them through {@link supportsOf} / {@link courierLeadOf}.
 */
export interface DeliveryProviderDefinition {
  id: DeliveryProviderType;
  /** The vendor's own name — shown as is, never translated. */
  name: string;
  /** Empty: Menteo holds the vendor's key — connecting is one click after the notice. */
  credentialFields: DeliveryCredentialField[];
  supports: DeliveryProviderSupports;
  /**
   * Minutes before the order is ready that Menteo asks a vendor without `HOLD` for the courier (its
   * trip to the branch overlaps the end of the preparation). 0 = when the prep time runs out.
   */
  courierLeadMinutes: number;
  /** `locations` keys of the branch's store-key input; the generic "API key de la tienda" when unset. */
  storeKeyLabelKey?: string;
  storeKeyHintKey?: string;
  storeKeyPlaceholderKey?: string;
  /**
   * How the key is typed: `password` (a secret, Picker's store API key — the default) or `text` (a
   * case-sensitive code the merchant was handed, Logística 21's clave: seeing it beats masking it).
   */
  storeKeyInputType?: 'password' | 'text';
  /** `locations` key of a linked branch's "store at the provider" line; "Tienda en {{provider}}" when unset. */
  linkedToKey?: string;
  /** `locations` key of what the merchant must know before connecting — shown again on the connected card. */
  connectNoticeKey?: string;
  /** `locations` key of the "after connecting" step; the generic "vinculá cada sucursal" when unset. */
  afterConnectKey?: string;
}

export const DELIVERY_PROVIDERS: readonly DeliveryProviderDefinition[] = [
  {
    id: 'picker',
    name: 'Picker',
    credentialFields: [
      {
        key: 'masterKey',
        labelKey: 'delivery.credentials.masterKey',
        hintKey: 'delivery.credentials.masterKeyHint',
        placeholderKey: 'delivery.credentials.masterKeyPlaceholder',
        type: 'password',
      },
    ],
    supports: {
      stores: true,
      cancelReasons: true,
      quote: true,
      hold: true,
      ownFleet: true,
      deliveryCode: false,
      coverage: false,
    },
    courierLeadMinutes: 0,
  },
  {
    id: 'logistica21',
    name: 'Logística 21',
    // Menteo's own key signs every call; the branch is identified by its "clave del comercio".
    credentialFields: [],
    supports: {
      stores: false,
      cancelReasons: false,
      quote: false,
      hold: false,
      ownFleet: false,
      deliveryCode: true,
      coverage: true,
    },
    courierLeadMinutes: 10,
    storeKeyLabelKey: 'delivery.bindings.merchantKey',
    storeKeyHintKey: 'delivery.bindings.merchantKeyHint',
    storeKeyPlaceholderKey: 'delivery.bindings.merchantKeyPlaceholder',
    storeKeyInputType: 'text',
    linkedToKey: 'delivery.bindings.linkedToMerchant',
    connectNoticeKey: 'delivery.connectNotice.logistica21',
    afterConnectKey: 'delivery.afterConnectMerchantKey',
  },
];

const NO_SUPPORTS: DeliveryProviderSupports = {
  stores: false,
  cancelReasons: false,
  quote: false,
  hold: false,
  ownFleet: false,
  deliveryCode: false,
  coverage: false,
};

/**
 * The registry entry for a provider `type`. An unknown type (a vendor the backend knows and this
 * build does not) still renders: its raw type as the name, the generic capabilities off.
 */
export function deliveryProviderDefinition(type: string | null | undefined): DeliveryProviderDefinition {
  const known = DELIVERY_PROVIDERS.find((provider) => provider.id === type);
  if (known) return known;
  const raw = type ?? '';
  return {
    id: raw as DeliveryProviderType,
    name: raw ? raw.charAt(0).toUpperCase() + raw.slice(1) : '—',
    credentialFields: [],
    supports: NO_SUPPORTS,
    courierLeadMinutes: 0,
  };
}

/**
 * Anything that names a provider: its `type`, a provider row (`type`), or a capabilities entry / a
 * bound branch (`provider`) — the last two carrying the API's `capabilities` when it sends them.
 */
export type DeliveryProviderRef =
  | string
  | null
  | undefined
  | { type: string; capabilities?: readonly string[] | null; courierLeadMinutes?: number | null }
  | { provider: string; capabilities?: readonly string[] | null; courierLeadMinutes?: number | null };

function refType(ref: DeliveryProviderRef): string | null | undefined {
  if (ref == null || typeof ref === 'string') return ref;
  return 'type' in ref ? ref.type : ref.provider;
}

/**
 * What a provider supports. The API's `capabilities` decide when the ref carries them (a non-empty
 * list); otherwise — a bare `type`, or an older API — the registry entry does. Every vendor-specific
 * branch in the UI reads this, so a vendor's behaviour is never keyed on its name.
 */
export function supportsOf(ref: DeliveryProviderRef): DeliveryProviderSupports {
  const capabilities = ref != null && typeof ref === 'object' ? ref.capabilities : null;
  if (!Array.isArray(capabilities) || capabilities.length === 0) {
    return deliveryProviderDefinition(refType(ref)).supports;
  }
  const has = (capability: DeliveryProviderCapability) => capabilities.includes(capability);
  return {
    stores: has('LIST_STORES'),
    cancelReasons: has('CANCEL_REASONS'),
    quote: has('QUOTE'),
    hold: has('HOLD'),
    ownFleet: has('OWN_FLEET'),
    deliveryCode: has('DELIVERY_CODE'),
    coverage: has('COVERAGE_CHECK'),
  };
}

/** The provider's quote answers coverage only (Logística 21): no `QUOTE`, but `COVERAGE_CHECK`. */
export function answersCoverageOnly(supports: DeliveryProviderSupports): boolean {
  return !supports.quote && supports.coverage;
}

/** The courier lead in minutes: the API's `courierLeadMinutes` when it sends one, else the registry's. */
export function courierLeadOf(ref: DeliveryProviderRef): number {
  const sent = ref != null && typeof ref === 'object' ? ref.courierLeadMinutes : null;
  if (typeof sent === 'number' && Number.isFinite(sent) && sent >= 0) return sent;
  return deliveryProviderDefinition(refType(ref)).courierLeadMinutes;
}

/**
 * The lead Menteo applies before the courier is asked for — only a vendor that does not hold the
 * booking itself has one (with `HOLD` the vendor waits out the prep time, and its lead is 0).
 */
export function localHoldLeadOf(ref: DeliveryProviderRef): number {
  return supportsOf(ref).hold ? 0 : courierLeadOf(ref);
}

/** The vendor name to show for a provider `type`. */
export function deliveryProviderName(type: string | null | undefined): string {
  return deliveryProviderDefinition(type).name;
}

/**
 * Per branch. `manual`: the branch runs its own fleet and the operator asks for a Picker rider on
 * demand. `auto`: Picker is the branch's delivery source — checkout quotes through it and a booking
 * is created when the order is accepted.
 */
export type DeliveryDispatchMode = 'manual' | 'auto';

/** How the rider is paid at the door: collects cash, nothing to collect, or carries a card terminal. */
export type DeliveryPaymentMode = 'cash' | 'online' | 'terminal';

/** Mirrors the backend's `DeliveryDispatchStatus` (spec §3). */
export type DeliveryDispatchStatus =
  | 'requested'
  | 'on_hold'
  | 'searching'
  | 'assigned'
  | 'at_pickup'
  | 'on_the_way'
  | 'at_dropoff'
  | 'delivered'
  | 'no_courier'
  | 'cancelled'
  | 'failed'
  | 'error';

/** A dispatch still in flight — anything before the rider handed it over or the run ended. */
export const ACTIVE_DISPATCH_STATUSES: readonly DeliveryDispatchStatus[] = [
  'requested',
  'on_hold',
  'searching',
  'assigned',
  'at_pickup',
  'on_the_way',
  'at_dropoff',
];

/** Ended without delivering: the operator has to look at it (no rider, cancelled, not delivered, never created). */
export const ATTENTION_DISPATCH_STATUSES: readonly DeliveryDispatchStatus[] = [
  'no_courier',
  'cancelled',
  'failed',
  'error',
];

/** The rider has the food and is heading to (or at) the customer's door — the board's "En camino". */
export const MOVING_DISPATCH_STATUSES: readonly DeliveryDispatchStatus[] = ['on_the_way', 'at_dropoff'];

/**
 * A rider has the booking, from assignment to the hand-over — mirrors the backend's
 * `RIDER_ASSIGNED_DISPATCH_STATUSES`. Only then is there a tracking link worth showing.
 */
export const RIDER_ASSIGNED_DISPATCH_STATUSES: readonly DeliveryDispatchStatus[] = [
  'assigned',
  'at_pickup',
  'on_the_way',
  'at_dropoff',
  'delivered',
];

export function isActiveDispatchStatus(status: DeliveryDispatchStatus | null | undefined): boolean {
  return !!status && ACTIVE_DISPATCH_STATUSES.includes(status);
}

export function hasAssignedRider(status: DeliveryDispatchStatus | null | undefined): boolean {
  return !!status && RIDER_ASSIGNED_DISPATCH_STATUSES.includes(status);
}

export function isAttentionDispatchStatus(status: DeliveryDispatchStatus | null | undefined): boolean {
  return !!status && ATTENTION_DISPATCH_STATUSES.includes(status);
}

export function isMovingDispatchStatus(status: DeliveryDispatchStatus | null | undefined): boolean {
  return !!status && MOVING_DISPATCH_STATUSES.includes(status);
}

/** `brand_delivery_provider.settings` (spec §3). */
export interface DeliveryProviderSettings {
  /** "Tengo flota propia en Picker": orders paid with a POS at the door can travel (as `terminal`). */
  myFleet: boolean;
  /** One WhatsApp message with the tracking link when the rider leaves. */
  notifyCustomerOnTheWay: boolean;
  /** Picker's "delivered" completes the order when it is still `confirmed`. */
  autoCompleteOnDelivered: boolean;
  /** Prep time a new binding starts with (the booking's `cookTime`). */
  defaultPrepTimeMinutes: number;
  /** Dispatch mode a new binding starts with. */
  defaultDispatchMode: DeliveryDispatchMode;
}

/** `location_delivery_binding.lastError` — free-form JSON on the backend; these are the fields we read. */
export interface DeliveryBindingError {
  message?: string;
  code?: string;
  at?: string;
  [key: string]: unknown;
}

/** One branch linked to one provider store (spec §4.5 `BindingDto`). The keys never leave the server. */
export interface DeliveryBindingDto {
  locationId: string;
  locationName: string | null;
  externalStoreId: string;
  externalStoreName: string;
  dispatchMode: DeliveryDispatchMode;
  /**
   * The EFFECTIVE prep time the booking waits (`cookTime`): the branch's own "time until the order
   * is ready" from Sucursales, else the brand's default. Read-only here — it is set in Sucursales.
   */
  prepTimeMinutes: number;
  /** Where {@link prepTimeMinutes} comes from. */
  prepTimeSource: 'location' | 'brand_default';
  webhookRegisteredAt: string | null;
  lastQuoteAt: string | null;
  lastError: DeliveryBindingError | null;
}

/** Why a brand cannot put a branch on `auto` dispatch. */
export type DeliveryAutoModeUnavailableReason = 'pos_pushes_orders';

/** `GET /delivery-providers/:brandId` row (spec §4.5 `DeliveryProviderResponseDto`). */
export interface DeliveryProviderResponseDto {
  id: string;
  type: DeliveryProviderType;
  enabled: boolean;
  settings: DeliveryProviderSettings;
  bindings: DeliveryBindingDto[];
  createdAt: string;
  /**
   * Whether a branch may run on `auto` dispatch. `false` when the brand's POS receives the orders
   * (it accepts them, not menteo, so no booking would ever be created on accept). Absent on an older
   * API — read it through {@link isAutoModeAvailable}, which treats that as available.
   */
  autoModeAvailable?: boolean;
  autoModeUnavailableReason?: DeliveryAutoModeUnavailableReason;
  /** What the vendor can do. Absent on an older API — read through {@link supportsOf}. */
  capabilities?: DeliveryProviderCapability[];
  /** See {@link DeliveryProviderDefinition.courierLeadMinutes}. Absent on an older API — {@link courierLeadOf}. */
  courierLeadMinutes?: number;
}

/** `auto` dispatch can be chosen for the brand's branches; a missing field (older API) means yes. */
export function isAutoModeAvailable(
  provider: Pick<DeliveryProviderResponseDto, 'autoModeAvailable'> | null | undefined,
): boolean {
  return provider?.autoModeAvailable !== false;
}

/** A store in the provider workspace (Picker `GET /store/all`). */
export interface DeliveryProviderStore {
  id: string;
  name: string;
  address?: string;
  /**
   * The workspace listing carries this store's API key, so linking by id is enough. `false` = the
   * operator has to paste the key (the row switches to the key input). Absent = unknown, try the id.
   */
  hasApiKey?: boolean;
}

export interface ValidateDeliveryProviderResult {
  valid: boolean;
  stores: DeliveryProviderStore[];
  /** The server's sentence for a refusal (`valid: false`), when it gives one. */
  message?: string;
}

/** Shaped by the provider's `credentialFields` — `{ masterKey }` for Picker. */
export type DeliveryProviderCredentials = Record<string, string>;

/**
 * `PATCH /delivery-providers/:id` body. 409 `DELIVERY_PAYMENT_METHOD_CONFLICT` when `myFleet` goes off
 * while the brand takes "Mandar POS" and a branch is on `auto` — resend with `removeSendPosForCard`.
 */
export interface UpdateDeliveryProviderBody {
  enabled?: boolean;
  settings?: Partial<DeliveryProviderSettings>;
  /** D7: the operator agreed to drop "Mandar POS" from the brand's delivery payment methods. */
  removeSendPosForCard?: boolean;
}

/** `POST /delivery-providers/:brandId/connect` body beyond the type and credentials (same 409 as the update). */
export interface ConnectDeliveryProviderOptions {
  settings?: Partial<DeliveryProviderSettings>;
  /** D7: the operator agreed to drop "Mandar POS" from the brand's delivery payment methods. */
  removeSendPosForCard?: boolean;
}

/** `PUT /delivery-providers/:brandId/bindings/:locationId` body. */
export interface SaveDeliveryBindingBody {
  /** A store from the workspace; its key is looked up server-side. */
  externalStoreId?: string;
  /** Or the branch's own store API key, pasted. */
  storeApiKey?: string;
  dispatchMode: DeliveryDispatchMode;
  /** D7: the operator agreed to drop "Mandar POS" from the brand's delivery payment methods. */
  removeSendPosForCard?: boolean;
}

/**
 * A provider quote (spec §4.1 `DeliveryQuoteService.quote`): `fee` is what the customer would be
 * charged and `providerFee` what Picker charges the business — the same gross figure (the customer
 * pays Picker's whole charge); `providerFeeBeforeTax` is its net part, when the provider gives it.
 * A vendor without `QUOTE` answers coverage only: `covered` and `distanceKm`, with `fee: null` — see
 * {@link quoteHasPrice}.
 */
export interface DeliveryQuote {
  covered: boolean;
  fee: number | null;
  etaMinutes?: number | null;
  distanceKm?: number | null;
  /** The provider's own radius from the branch (Logística 21's `radioMaximoKm`); null = no limit. */
  maxDistanceKm?: number | null;
  providerFee?: number | null;
  /** Picker's net `deliveryFee`: `providerFee` before tax. */
  providerFeeBeforeTax?: number;
  currency?: string | null;
  /** Why it is not covered, when the server says. */
  reason?: string | null;
  /**
   * The provider's own status and message when the quote FAILED ("Picker respondió 400: …") — what
   * tells "out of zone" apart from "the provider broke". Shown under the reason, never instead of it.
   */
  detail?: string | null;
}

/**
 * Why a quote came back `covered: false`. `out_of_zone` is the provider refusing the address and
 * `out_of_hours` refusing the moment (it is not operating now); the rest (`provider_unavailable`,
 * `bad_request`, `timeout`, `no_access`, anything unknown) are the quote itself failing — the booking
 * may well go through, so those never block a dispatch.
 */
export const QUOTE_OUT_OF_ZONE = 'out_of_zone';
export const QUOTE_OUT_OF_HOURS = 'out_of_hours';

/** A genuine coverage refusal of the address: it always blocks a rider request. */
export function isOutOfZoneQuote(quote: DeliveryQuote | null | undefined): boolean {
  return !!quote && !quote.covered && quote.reason === QUOTE_OUT_OF_ZONE;
}

/** The provider is not operating right now: blocks only a request that goes out now (see {@link quoteBlocksRequest}). */
export function isOutOfHoursQuote(quote: DeliveryQuote | null | undefined): boolean {
  return !!quote && !quote.covered && quote.reason === QUOTE_OUT_OF_HOURS;
}

/** The quote failed for any other reason: warn, but let the operator request the rider anyway. */
export function isFailedQuote(quote: DeliveryQuote | null | undefined): boolean {
  return !!quote && !quote.covered && quote.reason !== QUOTE_OUT_OF_ZONE && quote.reason !== QUOTE_OUT_OF_HOURS;
}

/**
 * Whether a refusal stops the rider request. Out of zone always does (the radius will not change).
 * Out of hours only when the request goes out now: a booking Menteo holds for later (`heldForLater`)
 * is checked again by the booking job when the hold ends, so then it is only a warning.
 */
export function quoteBlocksRequest(quote: DeliveryQuote | null | undefined, heldForLater: boolean): boolean {
  return isOutOfZoneQuote(quote) || (isOutOfHoursQuote(quote) && !heldForLater);
}

/**
 * A coverage refusal that is about the vendor's setup, not the address (Logística 21 does not know or
 * has disabled the branch's clave). Not blocking — like any other failed quote — but said apart.
 */
export const QUOTE_NO_ACCESS = 'no_access';

/**
 * The answer carries a price to show. A coverage-only answer (`fee: null`, a vendor without `QUOTE`)
 * never prints a money figure: its `providerFee` is null too, and reading it as 0 would show "$0".
 */
export function quoteHasPrice(quote: DeliveryQuote | null | undefined): quote is DeliveryQuote & { fee: number } {
  return !!quote && quote.covered && quote.fee != null;
}

/** 409 codes the delivery endpoints answer with (spec D7, §4.3). */
export const DELIVERY_PAYMENT_METHOD_CONFLICT = 'DELIVERY_PAYMENT_METHOD_CONFLICT';
export const DELIVERY_PAYMENT_METHOD_UNSUPPORTED = 'DELIVERY_PAYMENT_METHOD_UNSUPPORTED';
export const DELIVERY_PROVIDER_NOT_BOUND = 'DELIVERY_PROVIDER_NOT_BOUND';
/** A binding saved as `auto` on a brand whose POS receives the orders (`autoModeAvailable: false`). */
export const DELIVERY_AUTO_MODE_UNSUPPORTED = 'DELIVERY_AUTO_MODE_UNSUPPORTED';
/** A rider asked for an order whose customer has no usable phone, and none was sent with the request. */
export const DELIVERY_CUSTOMER_PHONE_REQUIRED = 'DELIVERY_CUSTOMER_PHONE_REQUIRED';
/** Connect for a brand that already has a delivery provider (one vendor per brand). */
export const DELIVERY_PROVIDER_ALREADY_CONNECTED = 'DELIVERY_PROVIDER_ALREADY_CONNECTED';

export function isDeliveryErrorCode(err: unknown, code: string): err is ApiError {
  return err instanceof ApiError && err.code === code;
}

/** The server's own sentence for a refusal (`message`), or null when it sent none. */
export function deliveryErrorMessage(err: unknown): string | null {
  if (err instanceof ApiError) {
    const message = err.data?.message;
    if (typeof message === 'string' && message.trim()) return message;
    if (Array.isArray(message) && typeof message[0] === 'string') return message[0];
    return null;
  }
  return null;
}

/**
 * Body + response helper shared by both delivery clients. Every helper in this app sends the session
 * cookie, raises `auth:unauthorized` on 401/403 and throws on non-OK — here as an `ApiError` so the
 * 409 codes above survive to the caller.
 *
 * Some agent controllers wrap their answers in `{ success, message, data }` and some answer bare; the
 * spec does not say which these use, so a wrapped body is unwrapped and a bare one passed through.
 */
export async function deliveryApi<T>(
  endpoint: string,
  options: { method?: 'GET' | 'POST' | 'PUT' | 'PATCH' | 'DELETE'; body?: unknown } = {},
): Promise<T> {
  const { method = 'GET', body } = options;
  const response = await fetch(`${API_BASE_URL}${endpoint}`, {
    method,
    credentials: 'include',
    headers: { 'Content-Type': 'application/json' },
    ...(body !== undefined && method !== 'GET' ? { body: JSON.stringify(body) } : {}),
  });

  if (response.status === 401 || response.status === 403) {
    window.dispatchEvent(new CustomEvent('auth:unauthorized', { detail: { status: response.status } }));
    throw new ApiError(response.status, `HTTP_${response.status}`);
  }
  if (!response.ok) throw await toApiError(response);
  if (response.status === 204) return undefined as T;

  const text = await response.text();
  if (!text) return undefined as T;
  return unwrapEnvelope(JSON.parse(text)) as T;
}

/** `{ success, data }` → `data`; anything else as is. */
export function unwrapEnvelope(body: unknown): unknown {
  if (
    body &&
    typeof body === 'object' &&
    !Array.isArray(body) &&
    'data' in body &&
    typeof (body as { success?: unknown }).success === 'boolean'
  ) {
    return (body as { data: unknown }).data;
  }
  return body;
}

export const deliveryProvidersClient = {
  list: (brandId: string) => deliveryApi<DeliveryProviderResponseDto[]>(`/delivery-providers/${brandId}`),

  /** Checks the master credentials and lists the workspace's stores (paginated server-side). */
  validate: (type: DeliveryProviderType, credentials: DeliveryProviderCredentials) =>
    deliveryApi<ValidateDeliveryProviderResult>('/delivery-providers/validate', {
      method: 'POST',
      body: { type, credentials },
    }),

  connect: (
    brandId: string,
    type: DeliveryProviderType,
    credentials: DeliveryProviderCredentials,
    { settings, removeSendPosForCard }: ConnectDeliveryProviderOptions = {},
  ) =>
    deliveryApi<DeliveryProviderResponseDto>(`/delivery-providers/${brandId}/connect`, {
      method: 'POST',
      body: {
        type,
        credentials,
        ...(settings ? { settings } : {}),
        ...(removeSendPosForCard ? { removeSendPosForCard } : {}),
      },
    }),

  update: (id: string, patch: UpdateDeliveryProviderBody) =>
    deliveryApi<DeliveryProviderResponseDto>(`/delivery-providers/${id}`, { method: 'PATCH', body: patch }),

  /** Soft delete: bindings go, dispatches already out keep being tracked. */
  disconnect: (id: string) => deliveryApi<void>(`/delivery-providers/${id}`, { method: 'DELETE' }),

  stores: (brandId: string) => deliveryApi<DeliveryProviderStore[]>(`/delivery-providers/${brandId}/stores`),

  /**
   * Links (or relinks) a branch. 409 `DELIVERY_PAYMENT_METHOD_CONFLICT` when `auto` would carry
   * "Mandar POS" without own fleet — resend with `removeSendPosForCard: true` once the operator agreed
   * (see {@link saveBindingResolvingConflict}). 409 `DELIVERY_AUTO_MODE_UNSUPPORTED` when `auto` is
   * sent for a brand whose POS receives the orders.
   */
  saveBinding: (brandId: string, locationId: string, body: SaveDeliveryBindingBody) =>
    deliveryApi<DeliveryBindingDto>(`/delivery-providers/${brandId}/bindings/${locationId}`, {
      method: 'PUT',
      body,
    }),

  removeBinding: (brandId: string, locationId: string) =>
    deliveryApi<void>(`/delivery-providers/${brandId}/bindings/${locationId}`, { method: 'DELETE' }),

  testQuote: (brandId: string, locationId: string, coords: { lat: number; lng: number }) =>
    deliveryApi<DeliveryQuote>(`/delivery-providers/${brandId}/bindings/${locationId}/test-quote`, {
      method: 'POST',
      body: coords,
    }),
};

/**
 * The D7 conflict round-trip, for every write that can raise it (a binding save, the provider's
 * settings, connect): the first attempt goes out as given; a `DELIVERY_PAYMENT_METHOD_CONFLICT` asks
 * `confirmRemoval` (the UI's dialog) and, only on yes, sends the same body again with
 * `removeSendPosForCard: true`. A no resolves `{ outcome: 'declined' }` and nothing is written. Every
 * other failure propagates.
 */
export async function writeResolvingSendPosConflict<B extends { removeSendPosForCard?: boolean }, R>(
  write: (body: B) => Promise<R>,
  body: B,
  confirmRemoval: () => Promise<boolean>,
): Promise<{ outcome: 'saved'; result: R; removedSendPos: boolean } | { outcome: 'declined' }> {
  try {
    return { outcome: 'saved', result: await write(body), removedSendPos: false };
  } catch (err) {
    if (!isDeliveryErrorCode(err, DELIVERY_PAYMENT_METHOD_CONFLICT)) throw err;
    if (!(await confirmRemoval())) return { outcome: 'declined' };
    return { outcome: 'saved', result: await write({ ...body, removeSendPosForCard: true }), removedSendPos: true };
  }
}

/** {@link writeResolvingSendPosConflict} for a binding save. */
export async function saveBindingResolvingConflict(
  save: (body: SaveDeliveryBindingBody) => Promise<DeliveryBindingDto>,
  body: SaveDeliveryBindingBody,
  confirmRemoval: () => Promise<boolean>,
): Promise<{ outcome: 'saved'; binding: DeliveryBindingDto; removedSendPos: boolean } | { outcome: 'declined' }> {
  const outcome = await writeResolvingSendPosConflict(save, body, confirmRemoval);
  if (outcome.outcome === 'declined') return outcome;
  return { outcome: 'saved', binding: outcome.result, removedSendPos: outcome.removedSendPos };
}

/** The bindings that can carry a rider right now: every binding of an ENABLED provider. */
export function activeBindings(providers: readonly DeliveryProviderResponseDto[] | undefined): DeliveryBindingDto[] {
  return (providers ?? []).filter((provider) => provider.enabled).flatMap((provider) => provider.bindings);
}
